import React from "react";
import { Utensils, MapPin, Clock, ExternalLink } from "lucide-react";
import { useTranslation } from 'react-i18next';

// Define the Restaurant interface
interface Restaurant {
  id: string;
  name: string;
  address: string;
  openingHours: string;
  link?: string;
  tags?: string[];
}

const IftarRestaurants = () => {
  const { t } = useTranslation();
  
  // Definiera restauranger utan översättningar i objektet
  const restaurants: Restaurant[] = [
    {
      id: "jalvi",
      name: "Jalvi Restaurang",
      address: "Dalvägen 1, 169 56 Solna, Sweden",
      openingHours: "17:00 - 22:00",
      link: "https://www.jalvirestaurang.se/",
      tags: ["buffet", "familyFriendly"]
    },
    {
      id: "anmol",
      name: "Anmol Sweets",
      address: "Fagerstagatan 13, Spånga, Sweden",
      openingHours: "16:00 - 21:00",
      link: "https://anmolsweets.se/",
      tags: ["indian", "vegetarian"]
    },
    {
      id: "folkets",
      name: "Folkets Kebab",
      address: "Hornsgatan 92, 118 21 Stockholm",
      openingHours: "16:30 - 23:00",
      link: "https://folketskebab.se/buffe/",
      tags: ["kebab", "buffet"]
    },
    {
      id: "sweeto",
      name: "Sweeto Salti",
      address: "Birger Jarlsgatan 93A, 11356 Stockholm",
      openingHours: "17:00 - 22:00",
      link: "https://www.sweetosalti.se/",
      tags: ["palestinian", "buffet"]
    },
    {
      id: "bab",
      name: "Bab Tuma",
      address: "Hallsta Gårdsgata 8, 721 38 Västerås",
      openingHours: "16:00 - 22:30",
      link: "https://babtuma.se/meny/",
      tags: ["middleEastern", "familyFriendly"]
    }
  ];

  return (
    <div className="w-full max-w-md mx-auto overflow-hidden animate-fadeIn shadow-lg rounded-lg bg-white dark:bg-gray-800">
      <div className="p-4">
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-4 rounded-lg mb-4">
          <div className="flex items-center justify-center gap-2">
            <Utensils className="h-5 w-5 text-purple-200" />
            <h2 className="text-xl font-bold">{t('restaurantsForIftar')}</h2>
          </div>
        </div>
        
        {restaurants.map((restaurant) => (
          <div 
            key={restaurant.id} 
            className="mb-4 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg"
          >
            <div className="flex justify-between items-start">
              <h3 className="font-semibold text-lg">{restaurant.name}</h3>
              {restaurant.tags && (
                <div className="flex flex-wrap gap-1">
                  {restaurant.tags.map((tag, tagIndex) => (
                    <span 
                      key={tagIndex} 
                      className="text-xs px-2 py-1 rounded-full bg-purple-100 dark:bg-purple-800/30 text-purple-700 dark:text-purple-300"
                    >
                      {t(tag)}
                    </span>
                  ))}
                </div>
              )}
            </div>
            
            <div className="mt-2 flex items-center text-xs text-gray-500 dark:text-gray-400">
              <MapPin size={14} className="mr-1" />
              <span className="mr-4">{restaurant.address}</span>
            </div>
            
            <div className="mt-1 flex items-center text-xs text-gray-500 dark:text-gray-400 mb-3">
              <Clock size={14} className="mr-1" />
              <span>{restaurant.openingHours}</span>
            </div>
            
            {restaurant.link && (
              <a 
                href={restaurant.link} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-purple-600 dark:text-purple-400 text-sm inline-flex items-center hover:underline"
              >
                <span className="mr-1">{t('visitWebsite')}</span>
                <ExternalLink size={14} />
              </a>
            )}
          </div>
        ))}
        
        <div className="flex justify-center gap-4 mt-6">
          <a 
            href="https://www.tiktok.com/tag/iftarrestaurants" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-purple-600 dark:text-purple-400 text-sm inline-flex items-center hover:underline"
          >
            <span className="mr-1">🎵</span> {t('restaurantsOnTikTok')}
          </a>
          <a 
            href="https://www.instagram.com/explore/tags/iftarrestaurants/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-purple-600 dark:text-purple-400 text-sm inline-flex items-center hover:underline"
          >
            <span className="mr-1">📸</span> {t('restaurantsOnInstagram')}
          </a>
        </div>
      </div>
    </div>
  );
};

export default IftarRestaurants;